<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div :class="sectionClass">
    <div class="multiselect-wrapper relative">
      <div
        class="custom-multiselect w-full"
        :class="{ 'p-invalid': validation?.$error, disabled: disabled }"
        @click="toggleDropdown"
        :aria-describedby="`${id}-help`"
      >
        <div
          class="selected-items flex items-center flex-wrap w-full p-2 text-sm text-gray-900 gap-2"
        >
          <span
            v-if="!selectedItems.length && placeholder"
            class="text-gray-500"
          >
            {{ placeholder }}
          </span>
          <div
            v-else-if="selectedItems.length <= 3"
            v-for="(item, index) in selectedItems"
            :key="index"
            class="selected-tag flex items-center rounded-full px-2 py-1 text-sm"
          >
            <span>
              {{ optionLabel ? item[optionLabel] : item }}
            </span>
            <button
              type="button"
              @click.stop="removeItem(item)"
              :disabled="disabled"
              style="cursor: pointer"
            >
              <i class="cancel-icon"></i>
            </button>
          </div>
          <div
            v-else
            key="data"
            class="selected-tag flex items-center rounded-full px-2 py-1 text-sm"
          >
            <span> {{ selectedItems.length }} Selected </span>
            <!-- <button
              type="button"
              class="ml-1 text-gray-500 hover:text-gray-700 focus:outline-none"
              @click.stop="removeItem(item)"
              :disabled="disabled"
            >
              ×
            </button> -->
          </div>
        </div>
        <img :src="iconSvg" alt="Dropdown icon" class="iconSvg h-4 ml-auto" />
      </div>

      <div
        v-if="showDropdown"
        class="dropdown-menu absolute w-full bg-white border border-gray-300 mt-1 max-h-60 overflow-y-auto"
      >
        <!-- Search -->
        <div
          class="search-wrapper p-2 border-b border-gray-200 flex items-center"
        >
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search..."
            class="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-500 text-sm"
          />
        </div>

        <!-- Options -->
        <div
          v-for="(option, index) in filteredOptions"
          :key="index"
          class="dropdown-item flex items-center p-2 cursor-pointer"
          :class="{
            'disabled-option': optionDisabled && optionDisabled(option),
            'selected-item-highlight': isSelected(option),
          }"
          @click="toggleOption(option)"
        >
          <input
            type="checkbox"
            :checked="isSelected(option)"
            :disabled="optionDisabled && optionDisabled(option)"
            class="custom-checkbox mr-2"
          />
          {{ optionLabel ? option[optionLabel] : option }}
        </div>
      </div>
    </div>

    <!-- Validation Messages -->
    <span
      v-if="validation?.$error && validation?.required?.$invalid"
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ PropName }} is required.
    </span>
    <span
      v-else-if="validation?.$error && validation?.requiredIf?.$invalid"
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ validation.requiredIf.$message.replace("value", PropName) }}
    </span>
  </div>
</template>

<script>
import iconSvg from "@/assets/icons/icon-down.svg"; // Adjust the path and file name as needed

export default {
  props: {
    sectionClass: { type: String, default: "field" },
    id: { type: String, required: true },
    validation: Object,
    options: { type: Array, default: () => [] },
    modelValue: { type: Array, default: () => [] },
    optionLabel: { type: String, default: "" },
    optionValue: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    optionDisabled: { type: Function, default: null },
    PropName: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      showDropdown: false,
      searchQuery: "",
      iconSvg,
    };
  },
  computed: {
    selectedItems() {
      if (this.optionValue) {
        return this.options.filter((option) =>
          this.modelValue.includes(option[this.optionValue])
        );
      }
      return this.modelValue;
    },
    selectedItemsLabel() {
      return this.selectedItems
        .map((item) => (this.optionLabel ? item[this.optionLabel] : item))
        .join(", ");
    },
    filteredOptions() {
      return this.options.filter((option) => {
        const label = this.optionLabel ? option[this.optionLabel] : option;
        return label.toLowerCase().includes(this.searchQuery.toLowerCase());
      });
    },
  },
  methods: {
    toggleDropdown() {
      if (!this.disabled) {
        this.showDropdown = !this.showDropdown;
      }
    },
    toggleOption(option) {
      if (this.optionDisabled && this.optionDisabled(option)) return; // Fixed typo: optionDisabled -> this.optionDisabled
      const value = this.optionValue ? option[this.optionValue] : option;
      let newValue = [...this.modelValue];
      if (newValue.includes(value)) {
        newValue = newValue.filter((item) => item !== value);
      } else {
        newValue.push(value);
      }
      this.$emit("update:modelValue", newValue);
    },
    removeItem(item) {
      const value = this.optionValue ? item[this.optionValue] : item;
      const newValue = this.modelValue.filter((val) => val !== value);
      this.$emit("update:modelValue", newValue);
    },
    isSelected(option) {
      const value = this.optionValue ? option[this.optionValue] : option;
      return this.modelValue.includes(value);
    },
    closeDropdown(event) {
      if (!this.$el.contains(event.target)) {
        this.showDropdown = false;
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.closeDropdown);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeDropdown);
  },
};
</script>

<style scoped lang="scss">
.custom-multiselect {
  display: flex;
  align-items: center;
  border: 1px solid #c0c0bc;
  border-radius: 0.25rem;
  min-height: 40px;
  width: 395px;
  font-size: 16px;
  justify-content: space-between;
  padding: 0 0.75rem;
  background: white;
  cursor: pointer;
}

.custom-multiselect.disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.selected-items {
  flex: 1;
  overflow: visible;
}

.selected-tag {
  border-radius: 12px;
  background: #f0f0ef;
  align-items: center;
  color: #374151;
  height: 30px;
}

.selected-tag button {
  padding: 0 4px;
  margin-top: 4px;
  border: none;
}

.placeholder {
  color: #6b7280;
}

.dropdown-menu {
  z-index: 1000;
  border-radius: 0.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  max-height: 240px;
}

.dropdown-item {
  height: 40px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  font-size: 14px;
  color: #374151;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.selected-item-highlight {
  background-color: #afd8c8;
  font-weight: 500;
  color: #374151;
}

.group-header {
  background-color: #f9fafb;
  font-size: 14px;
}

.custom-checkbox {
  accent-color: #43a584;
  width: 16px;
  height: 16px;
}

.disabled-option {
  background: #f5f5f5;
  color: #6b7280;
  cursor: not-allowed;
}

.p-invalid {
  border-color: #dc2626;
}

.p-error {
  color: #dc2626;
  font-size: 0.875rem;
}

.selected-items {
  flex: 1;
  overflow: hidden;
}
::deep(.text-sm) {
  font-size: 14.22px !important;
}
</style>
